import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { AuthSlice, createAuthSlice } from './slices/authSlice';
import { createNotificationSlice, NotificationSlice } from '@core/store/slices/notificationSlice';
import { createSettingsSlice, SettingsSlice } from '@core/store/slices/settingsSlice';
import { ApiSlice, createApiSlice } from '@core/store/slices/apiSlice';
import { PersistenceSlice, createPersistenceSlice } from '@core/store/slices/persistenceSlice';
import { createStatusDialogSlice, StatusDialogSlice } from '@core/store/slices/StatusDialogSlice.ts';
import { createDirtyGuardSlice, DirtyGuardSlice } from '@core/store/slices/dirtyGuardSlice.ts';
import {
    createUnsavedChangesDialogSlice,
    UnsavedChangesDialogSlice
} from '@core/store/slices/unsavedChangesDialogSlice.ts';

type StoreState = AuthSlice &
    NotificationSlice &
    SettingsSlice &
    ApiSlice &
    PersistenceSlice &
    StatusDialogSlice &
    DirtyGuardSlice &
    UnsavedChangesDialogSlice;

export const useStore = create<StoreState>()(
    devtools(
        persist(
            (set, get, api) => ({
                ...createAuthSlice(set, get, api),
                ...createNotificationSlice(set, get, api),
                ...createSettingsSlice(set, get, api),
                ...createApiSlice(set, get, api),
                ...createPersistenceSlice(set, get, api),
                ...createStatusDialogSlice(set, get, api),
                ...createStatusDialogSlice(set, get, api),
                ...createDirtyGuardSlice(set, get, api),
                ...createUnsavedChangesDialogSlice(set, get, api)
            }),
            {
                name: 'wanzl-connect-store',
                partialize: (state) => ({
                    selectedLocale: state.selectedLocale,
                    darkMode: state.darkMode,
                    cardsTableView: state.cardsTableView,
                    sidebarOpen: state.sidebarOpen
                })
            }
        ),
        {
            name: 'wanzl-connect-store'
        }
    )
);

// Convenience hooks for specific slices
export const useAuthStore = () =>
    useStore((state) => ({
        user: state.user,
        setUser: state.setUser,
        updateOrganisation: state.updateOrganisation,
        hasShopAccess: state.hasShopAccess,
        hasLicense: state.hasLicense,
        hasRole: state.hasRole,
        clearAuth: state.clearAuth,
        clearCache: state.clearCache,
        updateOrganisationClaim: state.updateOrganisationClaim
    }));

export const useNotificationStore = () =>
    useStore((state) => ({
        addNotification: state.addNotification,
        removeNotification: state.removeNotification,
        clearNotifications: state.clearNotifications
    }));

export const useSettingsStore = () =>
    useStore((state) => ({
        darkMode: state.darkMode,
        selectedLocale: state.selectedLocale,
        cardsTableView: state.cardsTableView,
        collapseSidebar: state.collapseSidebar,
        sidebarOpen: state.sidebarOpen,
        isMobile: state.isMobile,
        isShowFilterBar: state.isShowFilterBar,
        hasMobilePanel: state.hasMobilePanel,
        toggleDarkMode: state.toggleDarkMode,
        setLocale: state.setLocale,
        toggleCardsTableView: state.toggleCardsTableView,
        toggleSidebar: state.toggleSidebar,
        setSidebarOpen: state.setSidebarOpen,
        setIsMobile: state.setIsMobile,
        setIsShowFilterBar: state.setIsShowFilterBar,
        setHasMobilePanel: state.setHasMobilePanel
    }));

export const useStatusDialogStore = () =>
    useStore((state) => ({
        statusDialog: state.statusDialog,
        showStatusLoading: state.showStatusLoading,
        showStatusSuccess: state.showStatusSuccess,
        showStatusError: state.showStatusError,
        updateStatusDialog: state.updateStatusDialog,
        closeStatusDialog: state.closeStatusDialog
    }));

export const useApiStore = () =>
    useStore((state) => ({
        organisation: state.organisation,
        shopIds: state.shopIds,
        shopData: state.shopData,
        componentIds: state.componentIds,
        gatewayIds: state.gatewayIds,
        trolleyShelterIds: state.trolleyShelterIds,
        zoneTrackerIds: state.zoneTrackerIds,
        lineTrackerIds: state.lineTrackerIds,
        shopAutomationIds: state.shopAutomationIds,
        smartShelvesIds: state.smartShelvesIds,
        operationModes: state.operationModes,
        operationModeStateMap: state.operationModeStateMap,
        snmpDeviceTypes: state.snmpDeviceTypes,
        rfidTagProducts: state.rfidTagProducts,
        apiInfo: state.apiInfo,
        isLoading: state.isLoading,
        error: state.error,
        loadOrganisation: state.loadOrganisation,
        loadShopIds: state.loadShopIds,
        loadShopStatus: state.loadShopStatus,
        loadComponentIds: state.loadComponentIds,
        loadGatewayIds: state.loadGatewayIds,
        loadTrolleyShelterIds: state.loadTrolleyShelterIds,
        loadZoneTrackerIds: state.loadZoneTrackerIds,
        loadLineTrackerIds: state.loadLineTrackerIds,
        loadShopAutomationIds: state.loadShopAutomationIds,
        loadSmartShelvesIds: state.loadSmartShelvesIds,
        loadOperationModes: state.loadOperationModes,
        loadSnmpDeviceTypes: state.loadSnmpDeviceTypes,
        loadRfidTagProducts: state.loadRfidTagProducts,
        loadApiInfo: state.loadApiInfo,
        hasComponent: state.hasComponent,
        hasModule: state.hasModule,
        getModulesForShop: state.getModulesForShop,
        clearApiData: state.clearApiData
    }));

export const useDirtyGuardStore = () =>
    useStore((state) => ({
        registerDirtyGuard: state.registerDirtyGuard,
        clearDirtyGuard: state.clearDirtyGuard,
        isCurrentPageDirty: state.isCurrentPageDirty,
        guard: state.guard
    }));

export const useUnsavedChangesDialogStore = () =>
    useStore((state) => ({
        unsavedChangesRequest: state.unsavedChangesRequest,
        openUnsavedChangesDialog: state.openUnsavedChangesDialog,
        closeUnsavedChangesDialog: state.closeUnsavedChangesDialog
    }));
